import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {QueryFieldDrawer} from './QueryFieldDrawer';
import {t} from 'i18next';
import {Box, Grid, Typography} from '@mui/material';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../eca-doc-list/eca-save-search/EcaSaveSearch';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import AurLocationTreeView from '../../../eca-doc-list/eca-save-search/AurLocationTreeView';
import {useQueryExportField} from './useQueryExportField';
import {ecaExportResponseType} from '../../../eca-doc-list/eca-doc-list.util';
import {getUTCDateTimeForDisplay} from '../../../../utils/utils';
import {AurLightTooltip} from '../../../common/aur-light-tooltip/aur-light-tooltip';

interface IQueryFieldExportDrawerProps {
  open: boolean;
  defaultSelectedExportId?: string;
  onCancel?: () => void;
  onApply?: (exportData?: ecaExportResponseType | null) => void;
}

const QueryFieldExportDrawer: React.FC<IQueryFieldExportDrawerProps> = ({
  open,
  defaultSelectedExportId,
  onCancel,
  onApply,
}) => {
  const [isModal, setIsModal] = useState<boolean>(open);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedExportNode, setSelectedExportNode] =
    useState<TreeViewBaseItem<ExtendedTreeItemProps> | null>();

  const {loading, fields} = useQueryExportField();

  const allExport = useMemo(() => fields, [fields]);

  const selectedExport = useMemo(
    () => fields.find((item) => item.exportId === selectedExportNode?.id),
    [fields, selectedExportNode?.id]
  );

  const exportByGroup = useMemo(() => {
    const label = t('indexSearchPage.exportDrawer.allExports');
    return [
      {
        id: label,
        label,
        fileType: 'folder',
        children:
          allExport.length > 0
            ? allExport.map((itemChild) => {
                return {
                  id: itemChild.exportId,
                  label: itemChild.exportName,
                  count: itemChild.totalDocExported || 0,
                  ecaSearchId: itemChild.ecaExportId,
                  fieldId: label,
                  parentFolder: label,
                };
              })
            : undefined,
      },
    ];
  }, [allExport]);

  const filteredExportData = useMemo(
    () =>
      exportByGroup
        .map((item) => ({
          ...item,
          children:
            item.children?.filter((child) =>
              child.label.toLowerCase().includes(searchTerm.toLowerCase())
            ) || [],
        }))
        .filter((item) => item.children.length),
    [exportByGroup, searchTerm]
  );

  useEffect(() => {
    setIsModal(open);
  }, [open]);

  useEffect(() => {
    if (defaultSelectedExportId) {
      const getField = exportByGroup.find((item) =>
        item.children?.map((tag) => tag.id === defaultSelectedExportId)
      );
      if (getField) {
        const findExport = getField.children?.find(
          (fieldItem) => fieldItem.id === defaultSelectedExportId
        );
        setSelectedExportNode(findExport as never);
      }
    }
  }, [defaultSelectedExportId, exportByGroup]);

  const onCancelClicked = useCallback(() => {
    setIsModal(false);
    onCancel?.();
  }, [onCancel]);

  const onApplyClicked = useCallback(() => {
    setIsModal(false);
    onApply?.(selectedExport);
  }, [onApply, selectedExport]);

  const onSearch = useCallback((search?: string) => {
    setSearchTerm(search ?? '');
    setSelectedExportNode(undefined);
  }, []);

  const handleSelectedItem = (
    selectedNode: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => {
    if (selectedNode.fileType !== 'folder') {
      setSelectedExportNode({
        ...selectedNode,
      });
    }
  };

  const renderExports = useMemo(() => {
    if (loading) {
      return (
        <Box
          display={'flex'}
          flex={1}
          flexDirection={'column'}
          height={'100%'}
          bgcolor={'#182231'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <AurLoadingIndicator />
        </Box>
      );
    }

    return (
      <Box className={'query-export-field-container'}>
        <Grid container>
          <Grid item xs={7}>
            <Box className={'drawer-treeview-content custom-treeview'}>
              <AurLocationTreeView
                itemsList={filteredExportData as never}
                handleSelectedItem={handleSelectedItem}
                defaultExpandedIds={
                  defaultSelectedExportId
                    ? [t('indexSearchPage.exportDrawer.allExports')]
                    : undefined
                }
                selectedItem={defaultSelectedExportId || ''}
                preSelectedId={defaultSelectedExportId || ''}
                handleNoResultMessage={t(
                  'indexSearchPage.exportDrawer.noExport'
                )}
                allowGlobalSelect={true}
              />
            </Box>
          </Grid>

          <Grid item xs={5}>
            <Box className="export-info-container">
              <Box className={'export-divider'} />
              {selectedExport && (
                <Box className="export-info-view">
                  <Box gap={'8px'}>
                    <Typography className="typo-text subhead3_size color-mist-100">
                      {selectedExport.exportName}
                    </Typography>
                    <Typography className="typo-text body4_size color-mist-700">
                      {`${t('indexSearchPage.exportDrawer.exportId')} ${selectedExport.ecaExportId}`}
                    </Typography>
                  </Box>
                  <Typography className="typo-text label1_size color-grey-100">
                    {`${t('indexSearchPage.exportDrawer.docExported')} ${selectedExport.totalDocExported?.toLocaleString('en-US')}`}
                  </Typography>
                  <Box className={'sub-info'}>
                    <Box className={'create-info'}>
                      <Typography
                        aria-hidden="true"
                        className="typo-text body4_size color-mist-400 title-width"
                      >
                        {t('indexSearchPage.exportDrawer.exportedBy')}
                      </Typography>
                      <Box
                        aria-hidden="true"
                        display={'flex'}
                        flexDirection={'column'}
                      >
                        <Typography
                          aria-hidden="true"
                          className="typo-text body4_size color-mist-400"
                        >
                          {selectedExport.exportedBy}
                        </Typography>
                        <AurLightTooltip title={t('common.yearMonthDate')}>
                          <Typography
                            aria-hidden="true"
                            className="typo-text body4_size color-mist-400"
                          >
                            {getUTCDateTimeForDisplay(
                              selectedExport.exportDateTime
                            )}
                          </Typography>
                        </AurLightTooltip>
                      </Box>
                    </Box>
                    <Box className={'info-divider'} />
                    <Box className={'create-info'}>
                      <Typography
                        aria-hidden="true"
                        className="typo-text body4_size color-mist-400 title-width"
                      >
                        {t('indexSearchPage.exportDrawer.tagField')}
                      </Typography>
                      <Typography
                        aria-hidden="true"
                        className="typo-text body4_size color-mist-400"
                      >
                        {selectedExport.exportFieldUsed}
                      </Typography>
                    </Box>
                    <Box className={'info-divider'} />
                    <Box className={'create-info'}>
                      <Typography
                        aria-hidden="true"
                        className="typo-text body4_size color-mist-400 title-width"
                      >
                        {t('indexSearchPage.exportDrawer.tagUsed')}
                      </Typography>
                      <Typography
                        aria-hidden="true"
                        className="typo-text body4_size color-mist-400"
                      >
                        {selectedExport.tagUsed}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }, [defaultSelectedExportId, filteredExportData, loading, selectedExport]);

  return (
    <QueryFieldDrawer
      open={isModal}
      headerProps={{
        title: t('indexSearchPage.exportDrawer.exports'),
        isApplyDisabled: !selectedExport,
        onCancelClicked,
        onApplyClicked,
      }}
      searchProps={{
        title: t('indexSearchPage.exportDrawer.searchTitle'),
        onSearch,
      }}
    >
      {renderExports}
    </QueryFieldDrawer>
  );
};

export {QueryFieldExportDrawer};
